import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { searchPages } from './api';
import { useDomain } from './DomainContext';
import { derivePageState } from './pageStatus';
import Spinner from './ui/Spinner';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Badge from './ui/Badge';
import Alert from './ui/Alert';
import { openDevPreview } from './previewLinks';

// Dev preview links render only when a preview host is configured; never fall back to localhost.
const DEV_PREVIEW_HOST = (process.env.REACT_APP_DEV_PREVIEW_HOST || process.env.REACT_APP_DEV_HOST || '').replace(/\/$/, '');

// Media query hook for responsive design
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
};

const Search = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const perPage = 20;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read-only view of the global domain: this page's filter never mutates it.
  const { selectedDomain: globalDomain, domains, domainsLoaded } = useDomain();
  const defaultedRef = useRef(false);

  // Check if we're on mobile
  const isMobile = useMediaQuery('(max-width: 767px)');

  const handleEditPage = (page) => {
    // Extract domain ID from page data
    const domainId = page.domain_id;
    const pageId = page.slug || page._id || page.id;
    navigate(`/domain/${encodeURIComponent(domainId)}/pages/${encodeURIComponent(pageId)}/edit`);
  };

  // On first load with no filters in the URL, default to the globally selected domain (once).
  useEffect(() => {
    if (defaultedRef.current || !domainsLoaded) return;
    defaultedRef.current = true;
    if (globalDomain && !searchParams.get('domain_id') && !searchParams.get('q')) {
      setSearchParams({ domain_id: globalDomain.id, page: '1' }, { replace: true });
    }
  }, [domainsLoaded, globalDomain, searchParams, setSearchParams]);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError('');

    const query = searchParams.get('q') || '';
    const domainFilter = searchParams.get('domain_id') || '';
    const page = parseInt(searchParams.get('page') || '1', 10);

    try {
      const response = await searchPages(page, perPage, query, domainFilter);
      setPages(response.pages || []);
      setTotalResults(response.total || 0);
      setTotalPages(Math.ceil((response.total || 0) / perPage));

      // Handle API-level errors or messages
      if (response.error) {
        setError(response.error);
      } else if (response.message && response.total === 0) {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      setError('Failed to fetch pages. Please try again.');
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const queryFromUrl = searchParams.get('q') || '';
    const domainIdFromUrl = searchParams.get('domain_id') || '';
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);

    setSearchQuery(queryFromUrl);
    setSelectedDomain(domainIdFromUrl);
    setCurrentPage(pageFromUrl);

    fetchPages();
  }, [searchParams, fetchPages]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = {};
    if (searchQuery) newParams.q = searchQuery;
    if (selectedDomain) newParams.domain_id = selectedDomain;
    newParams.page = '1'; // Reset to first page on new search
    setSearchParams(newParams);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSelectedDomain('');
    setSearchParams({}); // Clear all URL parameters
  };

  const handlePageChange = (newPage) => {
    const newParams = {};
    if (searchQuery) newParams.q = searchQuery;
    if (selectedDomain) newParams.domain_id = selectedDomain;
    newParams.page = newPage.toString();
    setSearchParams(newParams);
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Prefer the loaded domain's hostname; fall back to the id-derived name.
  const getDomainName = (domainId) => {
    if (!domainId) return null;
    const found = domains.find((d) => d.id === domainId);
    return found?.hostname || domainId.replace(/_/g, '.');
  };

  const getPageUrl = (page) => {
    if (page.url) return page.url;
    const domainName = getDomainName(page.domain_id);
    if (domainName && page.slug) {
      return `https://${domainName}/${page.slug}`;
    }
    return '#';
  };

  const formatTags = (tags) => (Array.isArray(tags) ? tags.join(', ') : (tags || ''));

  // Shared per-row actions: labelled buttons, identical in the table and the mobile cards.
  const renderActions = (page) => (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditPage(page);
        }}
      >
        Edit
      </button>
      {page.domain_id && (
        <a
          className="btn btn-ghost btn-sm"
          href={`https://${getDomainName(page.domain_id)}/${page.slug || ''}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open the live page in a new tab"
        >
          View live
        </a>
      )}
      {page.slug && DEV_PREVIEW_HOST && (
        <a
          className="btn btn-ghost btn-sm"
          href={`${DEV_PREVIEW_HOST}/${page.slug}/?domain_id=${page.domain_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            // Carry a preview token so drafts and pending revisions render.
            e.preventDefault();
            openDevPreview(DEV_PREVIEW_HOST, page.domain_id, page.slug);
          }}
          title="Preview the page on the development host (drafts and pending revisions included)"
        >
          Preview
        </a>
      )}
    </>
  );

  const hasFilters = Boolean(searchQuery || selectedDomain);

  return (
    <div className="page">
      <PageHeader title="Pages" subtitle="Search every page across all sites" />

      {/* Search form */}
      <form onSubmit={handleSearch} className="toolbar">
        <select
          className="select"
          aria-label="Filter by domain"
          value={selectedDomain}
          onChange={(e) => {
            const newDomain = e.target.value;
            setSelectedDomain(newDomain);

            // Update URL parameters (local filter only; the global domain is untouched)
            const newParams = {};
            if (searchQuery) newParams.q = searchQuery;
            if (newDomain) newParams.domain_id = newDomain;
            newParams.page = '1'; // Reset to first page on domain change
            setSearchParams(newParams);
          }}
          style={{ minWidth: '200px' }}
        >
          <option value="">All domains</option>
          {domains.map((domain) => (
            <option key={domain.id} value={domain.id}>
              {domain.hostname || getDomainName(domain.id) || domain.id}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="input grow"
          aria-label="Search pages"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search pages by title, content, tags..."
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
        {hasFilters && (
          <button type="button" className="btn btn-ghost" onClick={handleClear}>
            Clear
          </button>
        )}
      </form>

      {/* Results info */}
      {!loading && pages.length > 0 && (
        <p className="muted" style={{ margin: '0 0 12px' }}>
          {searchQuery
            ? `Found ${totalResults} result${totalResults === 1 ? '' : 's'} for "${searchQuery}"`
            : `Showing ${totalResults} total pages`}
        </p>
      )}

      {/* Error message */}
      <Alert type="error" onDismiss={() => setError('')}>{error}</Alert>

      {/* Loading indicator */}
      {loading && <Spinner label="Loading pages…" />}

      {/* Results */}
      {!loading && pages.length > 0 && (
        <>
          {/* Desktop table */}
          {!isMobile && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>State</th>
                    <th>Domain</th>
                    <th>URL</th>
                    <th>Content preview</th>
                    <th>Tags</th>
                    <th>Updated</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page, index) => (
                    <tr key={page._id || index}>
                      <td style={{ maxWidth: '240px', overflowWrap: 'anywhere' }}>
                        <strong>{page.title || 'Untitled'}</strong>
                      </td>
                      <td>
                        <Badge state={derivePageState(page)} />
                      </td>
                      <td className="muted">
                        {getDomainName(page.domain_id) || 'N/A'}
                      </td>
                      <td style={{ maxWidth: '200px', overflowWrap: 'anywhere' }}>
                        <a href={getPageUrl(page)} target="_blank" rel="noopener noreferrer">
                          {page.slug || page.url || 'View'}
                        </a>
                      </td>
                      <td className="muted" style={{ maxWidth: '280px', overflowWrap: 'anywhere' }}>
                        {truncateText(page.introduction_md || page.content || '')}
                      </td>
                      <td className="muted" style={{ maxWidth: '160px', overflowWrap: 'anywhere' }}>
                        {formatTags(page.tags)}
                      </td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(page.updated_at)}
                      </td>
                      <td className="actions">
                        {renderActions(page)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile card layout */}
          {isMobile && (
            <div className="stack">
              {pages.map((page, index) => (
                <div key={page._id || index} className="card">
                  <div className="card-header" style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ margin: '0 0 4px', fontSize: '16px', overflowWrap: 'anywhere' }}>
                        {page.title || 'Untitled'}
                      </h3>
                      <div className="muted small">
                        {getDomainName(page.domain_id) || 'N/A'}
                      </div>
                    </div>
                    <Badge state={derivePageState(page)} />
                  </div>

                  <div className="small" style={{ marginBottom: '10px', overflowWrap: 'anywhere' }}>
                    <a href={getPageUrl(page)} target="_blank" rel="noopener noreferrer">
                      {page.slug || page.url || 'View'}
                    </a>
                  </div>

                  {(page.introduction_md || page.content) && (
                    <p className="muted" style={{ margin: '0 0 10px' }}>
                      {truncateText(page.introduction_md || page.content || '', 100)}
                    </p>
                  )}

                  <div className="muted small" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                    <span>{formatTags(page.tags) ? `Tags: ${formatTags(page.tags)}` : ''}</span>
                    <span>Updated {formatDate(page.updated_at)}</span>
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {renderActions(page)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* No results */}
      {!loading && pages.length === 0 && !error && (
        <EmptyState
          title={searchQuery ? 'No pages match your search' : 'No pages found'}
          message={
            searchQuery
              ? 'Try a different search term or choose another domain.'
              : selectedDomain
                ? 'This domain has no pages yet.'
                : 'No pages have been created yet.'
          }
          action={
            hasFilters ? (
              <button type="button" className="btn btn-secondary" onClick={handleClear}>
                Clear filters
              </button>
            ) : null
          }
        />
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="muted small">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Search;
